import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { AlcmeonCardFooter } from 'src/app/shared/components/card-footer/models/alcmeon-card-footer.model';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BasicService } from 'src/app/shared/services/basic.service';
import { FacebookService, WhatsappService } from 'src/app/shared/strategies/channels';
import { MassMessaging, MassMessagingStatusEnum } from '../../shared';
import { MassMessagingService } from '../mass-messaging.service';
import { RunningMassMessagingVolumesRefresher } from '../shared/running-mass-messaging-volumes-refresher.class';
import { massMessagingStatuses } from './shared/mass-messaging-statuses.const';

@Component({
  selector: 'app-mass-messaging-details',
  templateUrl: './mass-messaging-details.component.html',
  styleUrls: ['./mass-messaging-details.component.scss'],
})
export class MassMessagingDetailsComponent implements OnInit, OnDestroy {
  @Input() campaign: MassMessaging;

  backToLabel = this.basicService.translate('mass-messaging-details/back');
  massMessagingRefresher: RunningMassMessagingVolumesRefresher;
  footerContent: AlcmeonCardFooter;
  MassMessagingStatusEnum = MassMessagingStatusEnum;
  massMessagingStatuses = massMessagingStatuses;
  canResendCampaign: boolean;

  constructor(
    public massMessagingService: MassMessagingService,
    public dialog: MatDialog,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private customRouter: CustomRouter,
    private sitesService: SitesService,
    private whatsappService: WhatsappService,
    private facebookService: FacebookService,
    @Inject('MassMessagingFactory') private massMessagingFactory: any // Once v1 is deleted, move function in `MassMessagingService`.
  ) {}

  ngOnInit(): void {
    const { createdByLabel, modifiedByLabel, creation_time, update_time, errors, status } = this.campaign;
    this.footerContent = {
      createdBy: createdByLabel,
      createdAt: creation_time,
      modifiedBy: modifiedByLabel,
      modifiedAt: update_time,
    };
    this.canResendCampaign =
      errors &&
      [MassMessagingStatusEnum.CANCELLED_STATUS, MassMessagingStatusEnum.COMPLETED_STATUS].includes(status) &&
      this.alcmeonContextService.isAlcmeonUser();

    this.massMessagingFactory
      .getMassMessagingVolumes(this.alcmeonContextService.companyId, this.campaign)
      .then(({ data }: { data: MassMessaging }) => {
        this.massMessagingFactory.setUIVolumeFieldsFromRawData(this.campaign, data);
      });

    this.massMessagingRefresher = new RunningMassMessagingVolumesRefresher(
      this.alcmeonContextService.getCompany(),
      [this.campaign],
      this.massMessagingFactory
    );
    this.massMessagingRefresher.refreshVolumesOfCreatedAndRunningCampaign(); // computing volumes for each running campaign

    const { read } = this.massMessagingService.getDataFromAudienceFilters(this.campaign);
    if (!this.campaign.tmpFields) {
      this.campaign.tmpFields = {};
    }
    if (read) {
      this.campaign.tmpFields.read = read;
    }

    const { audience_filters } = this.campaign;

    const account = this.sitesService.sitesV2Map.get(+audience_filters.accounts[0]);
    if (account) {
      this.campaign.tmpFields.account = account;
      if ([this.whatsappService, this.facebookService].every(service => service.getType() !== account?.type)) {
        this.campaign.read = this.basicService.translate('not_applicable');
      }
    }
  }

  goBack(params?: { status: 0 | 1 | 2 }) {
    this.customRouter.go('auth.alcmeon.fuse.mass-messaging', params || null);
  }

  resendForUserInError(id: number) {
    const name = `alc-failed-campaign-${this.campaign.id}-retry`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'small-size',
      data: {
        title: this.basicService.translate('mass-messaging-details/resend/confirm/title'),
        description: this.basicService.translate('mass-messaging-details/resend/confirm/description', { name }),
        confirmBtnText: this.basicService.translate('yes'),
        cancelBtnText: this.basicService.translate('no'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.massMessagingService.resendForUserInError(id).subscribe({ next: () => this.goBack({ status: 0 }) });
      }
    });
  }

  ngOnDestroy(): void {
    try {
      this.massMessagingRefresher.cancelNextAutoRefreshIfAny();
    } catch {
      // Do nothing in case of issue.
    }
  }
}
